import React, { useEffect, useState } from 'react';
import { Clock, Maximize2, Minimize2, StopCircle } from 'lucide-react';
import { useKanbanTimeTracking } from '../../contexts/KanbanTimeTrackingContext';

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
}

/**
 * Popup global de rastreamento de tempo das cards do Kanban. Montado no
 * layout raiz (fora do switch de seções) para sobreviver à troca de tela.
 */
const KanbanTimeTrackingPopup: React.FC = () => {
  const {
    currentTrackedTaskTitle,
    activeStartTime,
    showTrackingPopup,
    isTrackingPopupMinimized,
    setIsTrackingPopupMinimized,
    stopTaskTracking
  } = useKanbanTimeTracking();

  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!activeStartTime) return;

    const tick = () => setElapsed(Math.floor((Date.now() - activeStartTime.getTime()) / 1000));
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [activeStartTime]);

  if (!showTrackingPopup || !activeStartTime) return null;

  if (isTrackingPopupMinimized) {
    return (
      <div className="fixed top-20 right-4 bg-white border shadow-lg rounded-full pl-3 pr-1.5 py-1.5 z-50 flex items-center gap-2">
        <Clock className="w-4 h-4 text-green-600" />
        <span className="text-sm font-mono">{formatElapsed(elapsed)}</span>
        <button
          onClick={() => setIsTrackingPopupMinimized(false)}
          className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
          title="Expandir"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => stopTaskTracking()}
          className="p-1 text-red-600 hover:text-red-700 rounded hover:bg-gray-100"
          title="Parar rastreamento"
        >
          <StopCircle className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed top-20 right-4 bg-white border shadow-lg rounded-lg p-4 z-50 w-64">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Rastreando tempo</span>
        <button
          onClick={() => setIsTrackingPopupMinimized(true)}
          className="text-gray-400 hover:text-gray-600"
          title="Minimizar"
        >
          <Minimize2 className="w-4 h-4" />
        </button>
      </div>
      <div className="text-xs text-gray-600 mb-2 truncate">{currentTrackedTaskTitle}</div>
      <div className="flex items-center justify-between">
        <span className="text-lg font-mono">{formatElapsed(elapsed)}</span>
        <button
          onClick={() => stopTaskTracking()}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-600 text-white hover:bg-red-700"
        >
          <StopCircle className="w-3.5 h-3.5" />
          Parar
        </button>
      </div>
    </div>
  );
};

export default KanbanTimeTrackingPopup;
